import { useMemo } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useParams } from 'react-router-dom'

import { analyser } from '../../analysis/engine'
import { episodesDerives } from '../../analysis/evenements'
import { NIVEAU_LABELS, VERDICT_LABELS, type Verdict } from '../../analysis/types'
import { db } from '../../db/db'
import { SYMPTOM_LABELS, type SymptomType } from '../../domain/types'
import { Carte, Entete, Puce, Vide } from '../composants'
import { formaterRR } from './Analyses'

const TON_VERDICT: Record<Verdict, 'ok' | 'alerte' | 'danger' | 'neutre'> = {
  'piste-serieuse': 'danger',
  'a-surveiller': 'alerte',
  'probablement-non-lie': 'ok',
  'donnees-insuffisantes': 'neutre',
}

const pct = (v: number) => `${(v * 100).toFixed(0)} %`

function formaterP(p: number | null): string {
  if (p === null) return 'non calcule'
  if (p < 0.001) return '< 0,001'
  return p.toFixed(3).replace('.', ',')
}

export function PageAssociation() {
  const { cle, symptome } = useParams()

  const donnees = useLiveQuery(
    async () => ({
      repas: await db.meals.toArray(),
      sensations: await db.sensations.toArray(),
      selles: await db.stools.toArray(),
      journaux: await db.dailyLogs.toArray(),
      ingredients: await db.ingredients.toArray(),
      recettes: await db.recipes.toArray(),
    }),
    [],
  )

  const resultat = useMemo(() => {
    if (!donnees) return undefined
    const { sensations, selles, ...reste } = donnees
    return analyser({ ...reste, episodes: episodesDerives(sensations, selles) })
  }, [donnees])

  const a = resultat?.associations.find(
    (x) => x.cle === decodeURIComponent(cle ?? '') && x.symptome === symptome,
  )

  if (!resultat) {
    return (
      <>
        <Entete titre="Détail" retour />
        <main className="contenu">
          <p className="doux">Calcul en cours...</p>
        </main>
      </>
    )
  }

  if (!a) {
    return (
      <>
        <Entete titre="Détail" retour />
        <main className="contenu">
          <Vide>Cette association n'existe plus dans les données actuelles.</Vide>
        </main>
      </>
    )
  }

  const ct = a.contingence

  return (
    <>
      <Entete
        titre={a.libelle}
        sousTitre={`${SYMPTOM_LABELS[a.symptome as SymptomType]} · ${NIVEAU_LABELS[a.niveau]}`}
        retour
        action={<Puce ton={TON_VERDICT[a.verdict]}>{VERDICT_LABELS[a.verdict]}</Puce>}
      />

      <main className="contenu">
        {a.verdict === 'donnees-insuffisantes' && a.motifInsuffisance && (
          <div className="encart encart--info">{a.motifInsuffisance}</div>
        )}

        {/* --- Resume --- */}
        <Carte titre="Ce que disent les données">
          <p style={{ marginBottom: 12 }}>
            Après un repas contenant <strong>{a.libelle}</strong>, un episode de{' '}
            {SYMPTOM_LABELS[a.symptome as SymptomType].toLowerCase()} a debute{' '}
            <strong>{a.fenetre.libelle} plus tard</strong> {pct(a.risqueExpose)} du temps, contre{' '}
            <strong>{pct(a.risqueNonExpose)}</strong> sans.
          </p>
          <p className="doux" style={{ marginBottom: 12 }}>
            C'est la fenêtre où l'écart est le plus marqué, parmi les quatre testées. Elle
            désigne un mécanisme : <strong>{a.fenetre.mecanisme.toLowerCase()}</strong>.
          </p>
          <div className="ligne ligne--espacee">
            <span className="doux">Risque relatif</span>
            <span className="gras mono" style={{ fontSize: '1.2rem' }}>
              {formaterRR(a.risqueRelatif)}
            </span>
          </div>
          <p className="tres-doux" style={{ marginTop: 8 }}>
            Intervalle de confiance à 95 % du risque exposé : {pct(a.icRisqueExpose.bas)} a{' '}
            {pct(a.icRisqueExpose.haut)}.
          </p>
        </Carte>

        {/* --- Profil de latence --- */}
        <Carte titre="Quand le symptôme apparaît">
          <p className="doux" style={{ marginBottom: 12 }}>
            Le délai n'est pas un détail : c'est ce qui distingue les mécanismes. Un pic net sur
            une seule fenêtre est un signal plus crédible qu'un effet étalé sur les quatre.
          </p>
          <div className="pile pile--serree">
            {a.profil.map((m) => {
              const retenue = m.fenetre.cle === a.fenetre.cle
              return (
                <div
                  key={m.fenetre.cle}
                  className="carte carte--plate"
                  style={{ padding: 10, opacity: m.exploitable ? 1 : 0.55 }}
                >
                  <div className="ligne ligne--espacee" style={{ marginBottom: 4 }}>
                    <span className={retenue ? 'gras' : undefined}>
                      {m.fenetre.libelle}
                      {retenue && ' ←'}
                    </span>
                    <span className="gras mono">
                      {m.exploitable ? formaterRR(m.risqueRelatif) : '—'}
                    </span>
                  </div>
                  <div className="tres-doux">{m.fenetre.mecanisme}</div>
                  <div className="tres-doux">
                    {m.exploitable
                      ? `${m.contingence.a}/${m.repasExposes} repas exposés · ${pct(m.risqueExpose)} contre ${pct(m.risqueNonExpose)}`
                      : 'Trop peu de repas exploitables dans cette fenêtre.'}
                  </div>
                </div>
              )
            })}
          </div>
          <p className="tres-doux" style={{ marginTop: 10 }}>
            Ces chiffres sont <strong>descriptifs</strong>. Le verdict porte sur le couple entier,
            jamais sur une fenêtre prise isolément : chercher dans quatre fenêtres est une seule
            question, et le test de permutation en paie déjà le coût.
          </p>
        </Carte>

        {/* --- Solidite --- */}
        <Carte titre="Solidité du signal">
          <div className="pile pile--serree">
            <Ligne
              label="Épisodes distincts imputés"
              valeur={String(a.episodesDistincts)}
              aide="Si peu d'épisodes portent tout le signal, celui-ci est fragile, même avec beaucoup de repas concernés."
            />
            <Ligne
              label="Repas exposés exploitables"
              valeur={`${a.repasExposes} sur ${a.repasExploitables}`}
              aide="Les repas pris pendant un épisode déjà en cours sont exclus : on ne peut pas y observer un nouveau début."
            />
            <Ligne
              label="p de Fisher"
              valeur={formaterP(a.pFisher)}
              aide="Probabilité d'observer un tel écart par hasard, si les repas étaient indépendants."
            />
            <Ligne
              label="q après correction (Fisher)"
              valeur={formaterP(a.qFisher)}
              aide="Corrige le fait que des centaines de couples aliment-symptôme sont testés simultanément."
            />
            <Ligne
              label="p de permutation"
              valeur={formaterP(a.pPermutation)}
              aide="Obtenue en décalant aléatoirement toute la chronologie des symptômes. Ne suppose pas l'indépendance des repas."
            />
            <Ligne
              label="q après correction (permutation)"
              valeur={formaterP(a.qPermutation)}
              aide="Critère de décision principal. Un couple n'est déclaré « piste sérieuse » que si cette valeur passe sous 0,05."
            />
          </div>
        </Carte>

        {/* --- Contingence --- */}
        <Carte titre="Tableau détaillé">
          <div className="defilable">
            <table className="table-contingence">
              <thead>
                <tr>
                  <th />
                  <th>Symptôme déclenché</th>
                  <th>Pas de déclenchement</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">Repas avec</th>
                  <td className="gras">{ct.a}</td>
                  <td>{ct.b}</td>
                </tr>
                <tr>
                  <th scope="row">Repas sans</th>
                  <td>{ct.c}</td>
                  <td>{ct.d}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Carte>

        {/* --- Indissociables --- */}
        {a.indissociablesDe.length > 0 && (
          <Carte titre="Attention : exposition indissociable">
            <p className="doux">
              Cet aliment est presque toujours consommé en même temps que{' '}
              {a.indissociablesDe.length} autre(s). Leurs statistiques sont identiques et rien ne
              permet de désigner le responsable. Pour trancher, il faudrait en consommer un sans
              l'autre.
            </p>
          </Carte>
        )}

        {/* --- Stratification --- */}
        {a.stratesStress && (
          <Carte titre="Et si c'était le stress ?">
            <p className="doux" style={{ marginBottom: 12 }}>
              La même association, recalculée séparément les jours calmes et les jours tendus. Si
              elle tient dans les deux cas, elle ne s'explique pas par le stress.
            </p>
            <div className="pile pile--serree">
              {a.stratesStress.map((s) => (
                <div key={s.libelle} className="ligne ligne--espacee">
                  <span className="doux">{s.libelle}</span>
                  {s.exploitable ? (
                    <span className="gras mono">{formaterRR(s.risqueRelatif)}</span>
                  ) : (
                    <span className="tres-doux">données insuffisantes</span>
                  )}
                </div>
              ))}
            </div>
          </Carte>
        )}

        {/* --- Effet-dose --- */}
        {a.dose && (
          <Carte titre="Effet-dose">
            <p className="doux" style={{ marginBottom: 12 }}>
              Taux de déclenchement selon la charge du repas. Une progression régulière est un
              argument nettement plus fort qu'une simple présence/absence — et colle à la réalité
              du SCI, où c'est la quantité cumulée qui compte.
            </p>
            <div className="pile pile--serree">
              {(['Charge faible', 'Charge moyenne', 'Charge élevée'] as const).map((lib, i) => (
                <div key={lib} className="ligne ligne--espacee">
                  <span className="doux">
                    {lib}{' '}
                    <span className="tres-doux">({a.dose!.effectifsParTertile[i]} repas)</span>
                  </span>
                  <span className="gras mono">{pct(a.dose!.tauxParTertile[i]!)}</span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 12 }}>
              {a.dose.monotone ? (
                <Puce ton="danger">Progression régulière avec la dose</Puce>
              ) : (
                <Puce>Pas de progression régulière</Puce>
              )}
            </div>
          </Carte>
        )}

        {/* --- Suite --- */}
        <Carte titre="Et maintenant ?">
          <p className="doux">
            Une statistique ne prouve pas une causalité. Pour confirmer une piste, la démarche
            reconnue est le test de réintroduction : retirer l'aliment deux à quatre semaines, puis
            le réintroduire seul, en quantité croissante, en continuant à tenir le journal. Si les
            symptômes disparaissent puis reviennent, la piste se confirme.
          </p>
          <p className="doux" style={{ marginTop: 10 }}>
            Ces résultats sont a discuter avec un medecin ou un dieteticien, en particulier avant
            d'exclure durablement une famille d'aliments.
          </p>
        </Carte>
      </main>
    </>
  )
}

function Ligne({ label, valeur, aide }: { label: string; valeur: string; aide?: string }) {
  return (
    <div>
      <div className="ligne ligne--espacee">
        <span className="doux">{label}</span>
        <span className="gras mono">{valeur}</span>
      </div>
      {aide && <p className="tres-doux">{aide}</p>}
    </div>
  )
}
